import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FileText, Check, Mail } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ExportButton } from "../components/ExportButton";
import { toast } from "sonner";

const MESI_ABBR = ["G", "F", "M", "A", "M", "G", "L", "A", "S", "O", "N", "D"];
const MESI_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function RiepilogoBuste() {
  const { selectedStructure } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const params = { anno, ...(selectedStructure && selectedStructure !== "all" ? { structure_id: selectedStructure } : {}) };
  const { data: rows = [] } = useQuery({ queryKey: ["buste-riep", anno, selectedStructure], queryFn: async () => (await api.get("/buste-paga/riepilogo", { params })).data });
  const anniOptions = Array.from(new Set([now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2, now.getFullYear() - 3])).sort((a, b) => b - a);
  const conMancanti = rows.filter((r) => r.mancanti.length > 0).length;
  const [meseInvio, setMeseInvio] = useState(now.getMonth() + 1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [invioBusy, setInvioBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [inviiOpen, setInviiOpen] = useState(false);
  const [invii, setInvii] = useState([]);
  const MESI_FULL_L = MESI_FULL;
  const openInvii = async () => {
    try { const r = await api.get("/buste-paga/invii", { params: selectedStructure !== "all" ? { structure_id: selectedStructure } : {} }); setInvii(r.data); } catch { setInvii([]); }
    setInviiOpen(true);
  };
  const presentiMese = rows.filter((r) => r.mesi_presenti.includes(meseInvio)).length;
  const inviaMassivo = async () => {
    setInvioBusy(true);
    try {
      const r = await api.post("/buste-paga/invia-massivo", { anno, mese: meseInvio, structure_id: selectedStructure !== "all" ? selectedStructure : undefined });
      setResult(r.data); setConfirmOpen(false);
      toast.success(`${r.data.inviate} buste inviate`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    setInvioBusy(false);
  };
  const bpCols = [
    { header: "Operatore", accessor: "operatore_nome" },
    ...MESI_FULL.map((mese, i) => ({ header: mese, accessor: (r) => (r.mesi_presenti.includes(i + 1) ? "Presente" : "Mancante") })),
    { header: "Stato", accessor: (r) => (r.mancanti.length === 0 ? "Completo" : `${r.mancanti.length} mancanti`) },
  ];

  return (
    <div>
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><FileText size={22} /> Riepilogo buste paga</h1>
          <p className="text-sm text-muted-foreground">{rows.length} operatori · {conMancanti} con buste mancanti nel {anno}</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs font-semibold mb-1 block">Invia buste del mese</Label>
            <div className="flex items-center gap-2">
              <select value={meseInvio} onChange={(e) => setMeseInvio(Number(e.target.value))} className="h-10 rounded-md border px-3 text-sm font-semibold" data-testid="riep-mese-invio">
                {MESI_FULL.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <Button onClick={() => setConfirmOpen(true)} data-testid="riep-invia-massivo-btn"><Mail size={15} className="mr-1" /> Invia a tutti</Button>
              <Button variant="ghost" onClick={openInvii} data-testid="riep-invii-btn">Storico invii</Button>
            </div>
          </div>
          <ExportButton filename={`riepilogo-buste-${anno}`} title={`Riepilogo buste paga ${anno}`} columns={bpCols} rows={rows} color="#1f7a5c" testid="riep-export" />
          <div>
            <Label className="text-xs font-semibold mb-1 block">Anno</Label>
            <select value={anno} onChange={(e) => setAnno(Number(e.target.value))} className="h-10 rounded-md border px-3 text-sm font-semibold" data-testid="riep-anno-select">
              {anniOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {rows.length === 0 && <div className="gr-card p-10 text-center text-muted-foreground">Nessun operatore nella selezione.</div>}

      <div className="gr-card p-0 overflow-x-auto">
        {rows.map((r) => (
          <div key={r.operatore_id} data-testid={`riep-row-${r.operatore_id}`} className="flex items-center gap-3 px-4 py-3 border-b last:border-0">
            <div className="w-48 shrink-0 font-semibold text-sm truncate"><button type="button" onClick={() => navigate(`/operatori/${r.operatore_id}`)} data-testid={`riep-nome-${r.operatore_id}`} className="text-primary hover:underline text-left truncate max-w-full" title="Apri la scheda dell'operatore">{r.operatore_nome}</button>{r.data_cessazione && <span className="block text-[10px] font-bold uppercase text-orange-700" data-testid={`riep-dimesso-${r.operatore_id}`}>Dimesso il {r.data_cessazione.slice(0, 10).split("-").reverse().join("/")}</span>}</div>
            <div className="flex items-center gap-1 flex-1">
              {MESI_ABBR.map((lbl, i) => {
                const m = i + 1; const ok = r.mesi_presenti.includes(m); const fuori = !ok && r.mesi_attivi && !r.mesi_attivi.includes(m);
                return (
                  <div key={m} title={`${MESI_FULL[i]}: ${ok ? "presente" : fuori ? "non in servizio" : "mancante"}`} data-testid={`riep-mese-${r.operatore_id}-${m}`}
                    className={`h-7 w-7 rounded grid place-items-center text-[11px] font-bold ${ok ? "bg-emerald-100 text-emerald-700" : fuori ? "bg-slate-50 text-slate-300 border border-dashed border-slate-200" : "bg-red-50 text-red-400 border border-red-200"}`}>
                    {ok ? <Check size={13} /> : lbl}
                  </div>
                );
              })}
            </div>
            <div className="w-24 shrink-0 text-right">
              {r.mancanti.length === 0
                ? <span className="text-xs font-bold text-emerald-700" data-testid={`riep-complete-${r.operatore_id}`}>Completo</span>
                : <span className="text-xs font-bold text-red-600" data-testid={`riep-mancanti-${r.operatore_id}`}>{r.mancanti.length} mancanti</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="riep-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} /> Invio massivo buste paga</DialogTitle>
            <DialogDescription>Verranno inviate via email le buste paga di <b>{MESI_FULL[meseInvio - 1]} {anno}</b> a tutti gli operatori che ne hanno una caricata e un'email registrata.</DialogDescription>
          </DialogHeader>
          <div className="text-sm rounded-lg border bg-secondary/40 p-3">Buste presenti per {MESI_FULL[meseInvio - 1]}: <b>{presentiMese}</b>. Gli operatori senza email verranno saltati e te lo segnalerò.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button onClick={inviaMassivo} disabled={invioBusy} className="font-semibold" data-testid="riep-confirm-send">{invioBusy ? "Invio in corso…" : "Invia ora"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!result} onOpenChange={(v) => !v && setResult(null)}>
        <DialogContent className="max-w-md" data-testid="riep-result-dialog">
          <DialogHeader><DialogTitle>Esito invio · {result?.periodo}</DialogTitle></DialogHeader>
          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border p-2 bg-emerald-50 border-emerald-200"><div className="text-2xl font-bold tabnum text-emerald-700" data-testid="riep-res-inviate">{result.inviate}</div><div className="text-[11px] font-semibold text-muted-foreground">Inviate</div></div>
                <div className="rounded-lg border p-2 bg-amber-50 border-amber-200"><div className="text-2xl font-bold tabnum text-amber-700">{result.senza_email.length}</div><div className="text-[11px] font-semibold text-muted-foreground">Senza email</div></div>
                <div className="rounded-lg border p-2 bg-red-50 border-red-200"><div className="text-2xl font-bold tabnum text-red-700">{result.errori.length}</div><div className="text-[11px] font-semibold text-muted-foreground">Errori</div></div>
              </div>
              {result.senza_email.length > 0 && <div className="text-xs"><b className="text-amber-700">Senza email (saltati):</b> {result.senza_email.join(", ")}</div>}
              {result.errori.length > 0 && <div className="text-xs"><b className="text-red-700">Errori di invio:</b> {result.errori.join(", ")}</div>}
              {result.totale === 0 && <div className="text-sm text-muted-foreground">Nessuna busta caricata per questo mese.</div>}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setResult(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviiOpen} onOpenChange={setInviiOpen}>
        <DialogContent className="max-w-2xl" data-testid="riep-invii-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail size={18} /> Storico invii buste paga</DialogTitle></DialogHeader>
          {invii.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="riep-invii-empty">Nessun invio registrato.</p>
          ) : (
            <div className="overflow-x-auto max-h-[440px]">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/60 text-left">
                  <th className="p-2 font-bold">Quando</th>
                  <th className="p-2 font-bold">Operatore</th>
                  <th className="p-2 font-bold">Email</th>
                  <th className="p-2 font-bold">Mese</th>
                  <th className="p-2 font-bold">Tipo</th>
                  <th className="p-2 font-bold">Esito</th>
                </tr></thead>
                <tbody>
                  {invii.map((r) => (
                    <tr key={r.id} className="border-t" data-testid={`riep-invio-${r.id}`}>
                      <td className="p-2 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}<div className="text-[11px] text-muted-foreground">{r.inviato_da}</div></td>
                      <td className="p-2 font-semibold">{r.operatore_nome}</td>
                      <td className="p-2 text-xs truncate max-w-[160px]">{r.email || "—"}</td>
                      <td className="p-2 whitespace-nowrap">{MESI_FULL_L[(r.mese || 1) - 1]} {r.anno}</td>
                      <td className="p-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.tipo === "massivo" ? "bg-blue-100 text-blue-700" : "bg-secondary text-foreground"}`}>{r.tipo}</span></td>
                      <td className="p-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.esito === "inviata" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.esito}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setInviiOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
